/**
 * buildReviewPrompt — pure function that assembles the Claude prompt for
 * full generation, single-field regeneration, and field refinement.
 * Extracted from AIReviewTab to keep file sizes within the 500-line limit.
 */
import {
  FINN_VALUES, BEHAVIORS, getBehaviorGroup, getRatingLabel, RATING_SCALE,
} from '../../data/managerReviewData';
import type { Employee, BehaviorData, Deliverable, FeedbackItem, LeadershipData } from '../../types/managerReviewV2';
import { LEADERSHIP_VALUE_NAMES, cultureAmpLeadershipAnswer, leadershipConfirmed } from './leadershipValues';
import { suggestedBehaviorRating } from './behaviorRating';
import type { PromotionChoice } from '../../types/managerReviewV2';

export interface BuildPromptData {
  employee: Employee;
  level: string;
  behaviorsData: Record<string, BehaviorData>;
  behaviorsRating: number | null;
  behaviorsRatingNote: string;
  deliverables: Deliverable[];
  deliveryRating: number | null;
  /** Q4 Leadership Values; absent or not applicable → no FIELD 4. */
  leadership?: LeadershipData;
  overallRating: number | null;
  overallRatingNote: string;
  promotion: PromotionChoice | null;
  promotionNote: string;
  biasNote: string;
  selfReview: string;
  feedbackItems: FeedbackItem[];
  /** Raw multi-reviewer performance-review feedback paste */
  perfFeedbackRaw?: string;
  /** Raw multi-reviewer anytime feedback paste */
  anytimeFeedbackRaw?: string;
}

export function buildReviewPrompt(
  data: BuildPromptData,
  mode: 'full' | 'quality' | 'delivery' | 'leadership' | 'behaviors' | 'calibration' | 'refine-delivery' | 'refine-leadership' | 'refine-behaviors' | 'refine-calibration' = 'full',
  refineOpts?: { currentText: string; instruction: string },
): string {
  const {
    employee, level, behaviorsData, behaviorsRating, behaviorsRatingNote,
    deliverables, deliveryRating, overallRating, overallRatingNote,
    promotion, promotionNote, biasNote, selfReview, feedbackItems,
    perfFeedbackRaw, anytimeFeedbackRaw, leadership,
  } = data;

  const hasLeadership = !!leadership && leadershipConfirmed(employee, leadership);
  const leadershipAnswer = leadership ? cultureAmpLeadershipAnswer(employee, leadership) : null;
  const lBlock = hasLeadership && leadership ? [
    `Culture Amp answer: ${leadershipAnswer ? leadershipAnswer.answer : 'not yet answered'}`,
    ...LEADERSHIP_VALUE_NAMES.map(v => `  ${v}: ${leadership.answers[v] === true ? 'yes, consistently' : leadership.answers[v] === false ? 'NOT consistently' : 'not answered'}`),
    leadership.wellDone.trim() ? `  Manager note — what went well: ${leadership.wellDone.trim()}` : '  Manager note — what went well: (nothing entered)',
    leadership.improvement.trim() ? `  Manager note — what to improve: ${leadership.improvement.trim()}` : '  Manager note — what to improve: (nothing entered)',
  ].join('\n') : null;

  const rl = (r: number | null) => r ? `${r}/5 — ${getRatingLabel(r)}` : 'not yet rated';

  // Compute suggested ratings (mirrors SummaryTab / BehaviorsTab logic)
  // Used to detect whether deviation-note fields are actually visible in the UI
  const suggestedBehaviorsRating = suggestedBehaviorRating(behaviorsData);
  const overallCalc = behaviorsRating && deliveryRating ? (behaviorsRating + deliveryRating) / 2 : null;
  const isTieCalc = overallCalc !== null && (overallCalc * 10) % 5 === 0 && !Number.isInteger(overallCalc);
  const suggestedOverallRating = isTieCalc ? deliveryRating : (overallCalc ? Math.round(overallCalc) : null);

  // Only include deviation notes when the note field is actually visible in the UI
  const activeOverallRatingNote = (overallRating && suggestedOverallRating && overallRating !== suggestedOverallRating)
    ? overallRatingNote : '';
  const activeBehaviorsRatingNote = (behaviorsRating && suggestedBehaviorsRating && behaviorsRating !== suggestedBehaviorsRating)
    ? behaviorsRatingNote : '';
  const activePromotionNote = promotion === 'yes' ? promotionNote : '';

  const filledDeliverablesCnt = deliverables.filter(d => d.title?.trim() || d.rating);

  const scaleText = RATING_SCALE.map(r => `${r.v} — ${r.label}: ${r.desc[0]}`).join('\n');

  const bGroup = getBehaviorGroup(level);
  const frameworkText = FINN_VALUES.map(v => {
    const bullets = BEHAVIORS[bGroup]?.[v.name] || [];
    return `${v.name} (${v.sub}):\n${bullets.map(b => `  • ${b.replace(/\*\*/g, '')}`).join('\n')}`;
  }).join('\n\n');

  const filledDeliverables = filledDeliverablesCnt;
  const dBlock = filledDeliverables.map((d, i) => {
    const lines = [
      `D${i + 1}: "${d.title?.trim() || 'Untitled'}" — Rating: ${rl(d.rating)}`,
      d.competencies?.length ? `  Competencies demonstrated: ${d.competencies.join(', ')}` : null,
      d.wellDone?.trim() ? `  Manager note — what went well: ${d.wellDone.trim()}` : '  Manager note — what went well: (nothing entered)',
      d.improvement?.trim() ? `  Manager note — what to improve: ${d.improvement.trim()}` : '  Manager note — what to improve: (nothing entered)',
    ];
    return lines.filter(Boolean).join('\n');
  }).join('\n\n');

  const bBlock = FINN_VALUES.map(v => {
    const d = behaviorsData[v.name];
    const strengths = d?.strengthsText?.trim() ? [d.strengthsText.trim()] : [];
    const improvements = d?.improvementsText?.trim() ? [d.improvementsText.trim()] : [];
    return [
      `${v.name}: ${rl(d?.rating ?? null)}`,
      strengths.length ? `  Manager notes — strengths:\n${strengths.map(s => `    + ${s}`).join('\n')}` : '  Manager notes — strengths: (nothing entered)',
      improvements.length ? `  Manager notes — growth areas:\n${improvements.map(s => `    - ${s}`).join('\n')}` : '  Manager notes — growth areas: (nothing entered)',
    ].filter(Boolean).join('\n');
  }).join('\n\n');

  const feedbackBlock = [
    selfReview?.trim() ? `Employee self-review:\n${selfReview.trim()}` : null,
    perfFeedbackRaw?.trim() ? `Peer & Upward Feedback:\n${perfFeedbackRaw.trim()}` : null,
    anytimeFeedbackRaw?.trim() ? `Anytime feedback:\n${anytimeFeedbackRaw.trim()}` : null,
    // Legacy individual feedback items (kept for backwards compatibility)
    feedbackItems?.filter(f => f.text?.trim()).map(f => `${f.type} feedback:\n"${f.text.trim()}"`).join('\n\n') || null,
  ].filter(Boolean).join('\n\n');

  const biasBlock = biasNote?.trim() ? `Manager self-reflection / potential blind spots:\n"${biasNote.trim()}"` : null;

  const contextBlock = `You are an expert HR coach helping a manager write a calibration-ready performance review for FINN (a car subscription company).

The review is addressed directly TO the employee: ${employee.name} (${employee.role}, Level: ${level}).

══ GROUNDING RULE — CRITICAL ══════════════════════════════════
Draw only from:
  1. The MANAGER INPUT provided (primary source for all review prose)
  2. The RATING SCALE definitions (to contextualise ratings)
  3. The TALENT FRAMEWORK definitions (reference context only — not to be used as a substitute for missing manager input)

If manager input is absent for a topic (no text notes — only a rating):
  → Write the literal string: [INSUFFICIENT DATA — add specific examples to make this section meaningful for the employee]
  → Do NOT complete, replace, or work around this placeholder yourself
  → Do NOT use talent framework language to fill in where real manager input is missing
  → Do NOT use multi-source feedback (peer, upward, self-review, anytime) as a substitute for missing manager notes — peer/anytime feedback is context only; specific behavioral examples in the generated text must be anchored in the manager's own notes
NEVER invent events, achievements, examples, or outcomes.
═══════════════════════════════════════════════════════════════

══ LENGTH & COVERAGE — CRITICAL (managers found earlier drafts too long and missing points) ══
• Every concrete point in the manager's notes — facts, numbers, names, examples, requests — must appear in the matching field. Keep numbers as the manager wrote them (digits stay digits). Shorten the wording, never drop a point. Exception: the manager's notes on a rating (overall or behaviors) are context for tone and emphasis only — never repeat, paraphrase or hint at their content in any generated field. A fact the manager also wrote in another note may still appear, sourced from that note.
• Add nothing the manager did not say: no invented impact, no computed percentages, no business value they did not name, no praise adjectives without a fact behind them.
• Every sentence carries a fact from the notes. Never repeat a point across sub-sections.
• Full sentences, no bullet lists — except the Calibration Summary (Field 13), which uses its two bullet lists. Respect the word limits of each field; shorter is better. If keeping every point needs more words than the limit, keeping every point wins — except Field 13, which keeps only the most important points within its own limits.
═══════════════════════════════════════════════════════════════

══ TONE & STYLE ════════════════════════════════════════════════
• For employee-facing fields (3, 4, 5), address the employee in SECOND PERSON — "you delivered", "your approach", "you demonstrated". The Calibration Summary (Field 13) is written in third person.
• Language must be inclusive and bias-free: avoid personality labels and vague praise. Do NOT flag gendered pronouns in the manager's raw notes — employee-facing output uses second person ("you"), so pronoun choice in the input is irrelevant.
• Tone: personal yet professional; direct but sensitive
• For positive feedback: be specific and appreciative — name exactly what was done well and why it mattered
• For constructive feedback: be clear about the gap, but frame it as a forward-looking growth opportunity
• Never use hollow filler phrases like "great communicator", "strong team player", or "very dedicated"
• Calibrate the strength/development balance per individual rating:
    1 – Not meeting:     ~15% strengths / ~85% development  |  language: corrective
    2 – Partially:       ~30% strengths / ~70% development  |  language: honest
    3 – Fully meeting:   ~55% strengths / ~45% development  |  language: directional
    4 – Exceeding:       ~70% strengths / ~30% development  |  language: aspirational
    5 – Outperforming:   ~80% strengths / ~20% development  |  language: ceiling-breaking — never corrective
• PROMOTION CONTEXT: CRITICAL: NEVER mention, hint at, or reference the promotion decision anywhere in the review text.
• COMPENSATION CONTEXT: CRITICAL: NEVER mention, hint at, or reference any compensation outcome, salary adjustment, merit increase, or key contributor bonus anywhere in the review text.
═══════════════════════════════════════════════════════════════

RATING SCALE:
${scaleText}

TALENT FRAMEWORK — ${level} Behavior Definitions:
${frameworkText}

══ MANAGER INPUT ════════════════════════════════════════════
EMPLOYEE CONTEXT:
  Previously promoted last cycle: ${employee.previouslyPromoted ? 'Yes' : 'No'}

OVERALL RATING: ${rl(overallRating)}${activeOverallRatingNote?.trim() ? `\n  Manager's note on overall rating (deviation from suggestion): "${activeOverallRatingNote.trim()}"` : ''}
DELIVERY RATING: ${rl(deliveryRating)}

DELIVERABLES:
${dBlock || '(no deliverables entered)'}
${lBlock ? `\nLEADERSHIP VALUES (the employee leads people):\n${lBlock}\n` : ''}
OVERALL BEHAVIORS RATING: ${rl(behaviorsRating)}${activeBehaviorsRatingNote?.trim() ? `\n  Manager's note on behaviors rating (${behaviorsRating !== null && suggestedBehaviorsRating !== null && behaviorsRating < suggestedBehaviorsRating ? 'rated below the suggested average' : 'deviation from suggestion'} — context for tone and emphasis, never quote it): "${activeBehaviorsRatingNote.trim()}"` : ''}

BEHAVIORS:
${bBlock}
${feedbackBlock ? `\nMULTI-SOURCE FEEDBACK:\n${feedbackBlock}` : ''}
${biasBlock ? `\n${biasBlock}` : ''}
PROMOTION DECISION: ${promotion === 'yes' ? 'Yes — nominated for promotion this cycle' : promotion === 'conditional' ? 'Conditional promotion in June 2027 — not nominated this cycle, reconsidered in June 2027' : promotion === 'no' ? 'No — not nominated' : 'Not yet decided'}${activePromotionNote?.trim() ? `\n  Manager's reasoning (context for tone calibration ONLY — NEVER reference in review text): "${activePromotionNote.trim()}"` : ''}
═══════════════════════════════════════════════════════════════`;

  const field3Block = `### FIELD 3: KEY DELIVERABLES
Write in second person, addressed directly to the employee. Use this exact structure:

1. Opening statement — one sentence (max 25 words) naming the overall pattern.

2. For EACH deliverable, a titled block:
   **[Deliverable title]**
   **Achieved Impact:** [what you accomplished, as the manager described it — max 35 words]
   **Demonstrated Strengths:** [the behaviours behind it, referencing tagged competencies — max 30 words]
   **Opportunities for Improvement:** [every point from the "what to improve" note, forward-looking — max 40 words]

3. Closing statement — one sentence (max 25 words).

Opening and closing only summarise what the blocks say — no new claims (e.g. no "faster than planned" unless a note says so).
If the notes give nothing for one sub-section of a deliverable, leave that sub-section out instead of filling it.
Use [INSUFFICIENT DATA — …] only INSIDE a deliverable block whose manager notes are BOTH empty — never as an extra line elsewhere.

If manager notes are absent for a deliverable (no wellDone / improvement text), write [INSUFFICIENT DATA — add specific examples to make this section meaningful]. Do NOT pad with framework language, generic statements, or inferred observations.`;

  const field4Block = `### FIELD 4: LEADERSHIP VALUES
Write in second person, addressed directly to the employee. Answer the form's question: where did you demonstrate FINN's three Leadership Values (Deliver Exceptional Results, Fuel Talent, Push FINN to the next Level) in line with your Talent Framework, and where could you have been stronger?
Two labeled sub-sections:
  **Strengths:** [where the values showed up, from the manager's "went well" note — max 50 words]
  **Opportunities for growth:** [where they could have been stronger, from the "to improve" note — cover every value answered NOT consistently — max 50 words]
Also use the Delivery manager notes: if a deliverable note describes the employee leading people in a way that matches one of the three Leadership Values (e.g. setting standards for their team, hiring, coaching or growing someone, giving their team direction), bring that example in here too, in the matching sub-section. It may appear in both fields. Only examples the manager wrote — never infer leadership from results alone.
Check each sub-section on its own: Opportunities for growth needs a written growth example (the "to improve" note, or a leadership example in a deliverable's "could have gone better" note); a positive Delivery example does not count for it. Without one, do NOT write growth points from the Yes/No answers alone: write [INSUFFICIENT DATA — add examples for the values answered NOT consistently]. Same for Strengths: it needs the "went well" note or a positive leadership example from Delivery.
Draw on the SBIR model as a guide (Situation, Behaviour, Impact, Request) — natural prose, not a template.
If both Leadership notes are empty and no Delivery note gives a leadership example, write [INSUFFICIENT DATA — add specific examples]. Do not complete this placeholder yourself.
Inclusive language throughout. No filler. Always write in English.`;

  const field5Block = `### FIELD 5: BEHAVIORS
Write in second person, addressed directly to the employee. Cover all 5 FINN values in order.
Open each value section with its name as a bold heading — e.g. **Aim High**.
Structure each value section with two labeled sub-sections:
  **Strengths:** [what the employee did well, from the manager's notes for that value — max 35 words]
  **Opportunities for growth:** [every point from the "growth areas" note, framed forward-looking — max 30 words]
If a value has strengths notes but no growth-area notes, write only the Strengths sub-section (and vice versa) — do not invent the missing side.
Draw on the SBIR model as a guide (Situation, Behaviour, Impact, Request) — write as natural prose, not as a rigid template.
Anchor Strengths and Opportunities in the manager's notes for that value, using the framework behaviors only to name what was shown.
If a value has no manager notes (only a rating, or nothing at all), write [INSUFFICIENT DATA — add specific examples] for the entire value block. Do not complete this placeholder yourself.
Inclusive language throughout. No sub-ratings. No filler. Always write in English.`;

  const field13Block = `### FIELD 13: CALIBRATION SUMMARY
Not visible to the employee — written for the calibrators who prepare the calibration discussion. Third person ("they"), not second person.
Based on the manager input above, summarise the employee's strengths and development areas across Delivery and Behavior. Use EXACTLY this format:
**Strengths:**
- [one line, max 20 words, a concrete point from the notes]
(1–3 bullets)
**Development areas:**
- [one line, max 20 words, a concrete point from the notes]
(1–3 bullets)
Pick the points that matter most for the ratings. Max 3 bullets per list — never more. Nothing the manager did not say. Here the rating notes (deviation, rated below the suggestion) MAY inform the choice of points, but never quote them.`;

  if (mode === 'calibration') {
    return `${contextBlock}\n\nWrite ONLY the Calibration Summary field. Begin your response with the marker ### FIELD 13: and output nothing else.\n\n${field13Block}`;
  }

  if (mode === 'delivery') {
    return `${contextBlock}\n\nRegenerate ONLY the Key Deliverables field. Begin your response with the marker ### FIELD 3: and output nothing else.\n\n${field3Block}`;
  }

  if (mode === 'leadership') {
    return `${contextBlock}\n\nRegenerate ONLY the Leadership Values field. Begin your response with the marker ### FIELD 4: and output nothing else.\n\n${field4Block}`;
  }

  if (mode === 'behaviors') {
    return `${contextBlock}\n\nRegenerate ONLY the Behaviors field. Begin your response with the marker ### FIELD 5: and output nothing else.\n\n${field5Block}`;
  }

  if ((mode === 'refine-delivery' || mode === 'refine-leadership' || mode === 'refine-behaviors' || mode === 'refine-calibration') && refineOpts) {
    const fieldLabel = mode === 'refine-delivery' ? 'Key Deliverables (Field 3)'
      : mode === 'refine-leadership' ? 'Leadership Values (Field 4)'
      : mode === 'refine-calibration' ? 'Calibration Summary (Field 13 — third person, max 3 bullets per list)' : 'Behaviors (Field 5)';
    return `${contextBlock}

You are refining the ${fieldLabel} section based on the manager's instruction.

══ CURRENT FIELD TEXT ═══════════════════════════════════════════
${refineOpts.currentText}
═══════════════════════════════════════════════════════════════

══ MANAGER'S REFINEMENT INSTRUCTION ══════════════════════════
"${refineOpts.instruction}"
═══════════════════════════════════════════════════════════════

Apply the instruction. Rules:
1. Stay strictly within the grounding rule — do not invent facts not present in the manager input
2. Maintain the required person for the field (second person for Fields 3, 4 and 5; third person for Field 13) and all tone requirements
3. Never reference or hint at the promotion decision or any compensation outcome (merit increase, key contributor bonus, salary adjustment)
4. If the instruction violates these rules, respond with EXACTLY:
   RULES_VIOLATION: [one sentence explaining which rule is violated]
5. Otherwise output the refined field text directly — no preamble, no field markers`;
  }

  const qualityBlock = `## SECTION 1: REVIEW QUALITY

List each issue on a new line using EXACTLY this format (no brackets, no other punctuation):
TIER CATEGORY — Action sentence (max 12 words). → Location

TIER is either BLOCKER or SUGGESTION:
- BLOCKER: INCONSISTENCY, BIAS, INCOMPLETE — things that will be challenged in calibration or make the review factually wrong
  Special rule: if an ENTIRE FINN value section has no manager input at all (no rating, no strengths text, no improvements text), flag as BLOCKER INCOMPLETE — a whole missing value cannot produce meaningful review content.
- SUGGESTION: MISSING EVIDENCE, LACK OF CONTENT, VAGUE LANGUAGE — things that improve quality but manager can decide on

CATEGORY is one of: INCONSISTENCY, BIAS, INCOMPLETE, MISSING EVIDENCE, LACK OF CONTENT, VAGUE LANGUAGE

HARD RULE — VAGUE LANGUAGE only applies when a manager note is essentially content-free (e.g. "did well", "could improve", "good"). Do NOT flag notes that are specific but awkwardly phrased — the review generator will rephrase raw notes into polished prose automatically.

HARD RULE — A rating that differs from its suggested average is NOT an inconsistency when the manager's note on that rating is present: the note IS the explanation. Only flag INCONSISTENCY for a deviation without a note. A behaviors rating ABOVE its suggested average needs no note — never flag it.

HARD RULE — Do NOT flag VAGUE LANGUAGE on "Manager's note on overall rating (deviation from suggestion)" or "Manager's note on behaviors rating" fields (labelled either "rated below the suggested average" or "deviation from suggestion"). These are brief administrative explanations for a rating deviation, not employee-facing content, and are never seen in calibration.

Action sentence: max 12 words, start with a verb, be specific (not "add more detail" — say exactly what to add).

Location (optional, include when issue maps to a specific place):
- "→ Delivery: [deliverable title]" for individual deliverable issues
- "→ Delivery" for overall delivery rating issues (the delivery rating dropdown is in the Delivery tab)
- "→ Leadership Values" for Leadership Values issues (Q4)
- "→ Behaviors: [value name]" for individual behavior value issues
- "→ Behaviors" for overall behaviors rating issues (the behaviors rating dropdown is in the Behaviors tab)
- "→ Summary" for overall performance rating or promotion decision issues
- "→ Before You Start" for feedback quality issues

HARD RULE — NEVER flag gendered pronouns (he/she/his/her/they) in manager notes as [BIAS] or any other category. The generated review output is always in second person ("you"), making pronoun choice in the manager's raw input completely irrelevant. If the only bias issue you would raise is pronoun usage, output no [BIAS] item at all.

If no issues: write only "No issues found."`;

  // The quality check runs as its own call next to one call per field (AIReviewTab): separate
  // calls cannot cut each other off at the token limit, and each field gets the model's full attention.
  if (mode === 'quality') {
    return `${contextBlock}\n\nReturn ONLY the review quality check, starting with the header below.\n\n${qualityBlock}`;
  }

  return `${contextBlock}

Return EXACTLY two sections with these headers:

${qualityBlock}

## SECTION 2: CULTURE AMP — READY TO COPY

${field3Block}
${hasLeadership ? `\n${field4Block}\n` : ''}
${field5Block}

${field13Block}`;
}
